// @ts-strict-ignore
import omit from 'lodash/omit';
import produce from 'immer';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';

import {
  Entry,
  FormulaItem,
  isFormulaEntry,
  MetricEntry,
  QueryFormulaEntry,
  QueryMetricEntry,
  QuerySuperEntry,
} from 'common/query-builder/query-entry-section/types';
import {getShowSection} from 'common/report/insights/util/events';
import {BehaviorShowClause, FormulaShowClause, ShowClauseFormula} from 'common/report/insights/util/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {Metric} from 'app_api/projects/metrics/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {getEntries} from 'common/report/insights/util/events';
import {
  getResolvedFormulaEntry,
  getResolvedFormulaShowClause as formulaUtil_getResolvedFormulaShowClause,
  isModifiedShowClauseFormula,
} from 'common/util/formulas';
import {Model, ModelStatus} from 'common/util/model-resource/model-resource';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {getUserAccess} from 'common/report/util/saved-entities';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import type {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {RestrictEntitySaving} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {LocalizationStore} from 'common/i18n/store';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';

import formulaParser from './formula-parser-compiled';

const {get_parser: getFormulaParser, Token, UnexpectedCharacters, UnexpectedToken} = formulaParser;

const DefaultFormulaNamePrefix = `Formula`;

type RowProps = EntryRowProps<QueryFormulaEntry>;

export function getNextFormulaName({existingFormulaNames}: {existingFormulaNames: string[]}): string {
  const getFormulaNumber = () => {
    const defaultFormulaNameRegex = RegExp(`^${DefaultFormulaNamePrefix} (\\d+)$`);
    const existingFormulaNumbers = existingFormulaNames
      .map((name) => name.match(defaultFormulaNameRegex)?.[1])
      .filter((numStr) => numStr)
      .map((numStr) => Number(numStr));
    return Math.max(existingFormulaNames.length, ...existingFormulaNumbers) + 1;
  };

  return existingFormulaNames.length === 0
    ? DefaultFormulaNamePrefix
    : `${DefaultFormulaNamePrefix} ${getFormulaNumber()}`;
}

const debouncedAddToast = debounce(addToast, 1000);

export function getFormulaShowClauseTrackingProps({
  formulaShowClause,
  metricsResource,
  behaviorsResource,
  i18n,
}: {
  formulaShowClause: FormulaShowClause;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  i18n: LocalizationStore;
}) {
  const resolvedFormula = formulaUtil_getResolvedFormulaShowClause({
    metricsResource,
    behaviorsResource,
    formulaShowClause,
  });
  if (!resolvedFormula) {
    const errorMessage = i18n.t(`query-builder.errors.failed-to-resolve-formula`);
    reporter.error(errorMessage, {formulaShowClause});
    debouncedAddToast({message: errorMessage, type: ToastType.ERROR});
    return {};
  }
  const metric = metricsResource.getOne(resolvedFormula.id);
  const operatorsCount = (resolvedFormula.definition?.match(/\*|\/|\+|-/g) || []).length;
  const operandsCount = (resolvedFormula.definition?.match(/[A-Z]+/g) || []).length;

  return {
    name: resolvedFormula.name,
    definition: resolvedFormula.definition,
    id: resolvedFormula.id,
    'is expanded': formulaShowClause.isExpanded,
    'is hidden': formulaShowClause.isHidden,
    'is cumulative': Boolean(formulaShowClause.measurement?.cumulative),
    'is rolling': Boolean(formulaShowClause.measurement?.rolling),
    'is edited': isModifiedShowClauseFormula(formulaShowClause),
    '# of metrics': resolvedFormula.referencedMetrics?.length,
    'referenced event names': resolvedFormula.referencedMetrics
      ?.filter((referencedMetric) => referencedMetric.behavior.type === MetricType.Event)
      .map((referencedMetric) => referencedMetric.behavior.name),
    'types of metrics': Array.from(
      new Set(resolvedFormula.referencedMetrics?.map((referencedMetric) => referencedMetric.behavior.type)),
    ),
    '# of operators': operatorsCount,
    '# of operands': operandsCount,
    ...(metric.status === ModelStatus.Success
      ? {
          'permission level': getUserAccess(metric, {
            ownerId: metric.value.created_by.id,
            userId: userCookieUtils.getUserIdentityFromCookie()?.id,
          }),
        }
      : {}),
  };
}

/**
 * For a formula and all the metrics it references, we need to map
 * existing positions in the report entries to what they would become
 * inside a saved formula referenced metrics array.
 *
 * For example, if a report has 3 metrics and an inline formula only references metric `C`,
 * when saving this formula, the variable `C` will now become `A` and this metric will
 * be index 0 in the `referencedMetrics` field of the saved formula
 */
export function getUpdatedFormulaAndMetrics(
  formula: ShowClauseFormula,
  entries: Array<QueryMetricEntry | MetricEntry>,
): {definition: string; referencedMetrics: BehaviorShowClause[]} {
  const formulaOperands = new Set(formula.definition.match(/[A-Z]{1,}/g));

  // a map of the metrics referenced by the inline formula
  // to the variable identifier they are currently using
  const referencedEntries: Array<QueryMetricEntry | MetricEntry> = [];
  const updatedFormulaMap: Record<string, string> = {};

  for (const [index, entry] of entries.entries()) {
    const identifier = getAlphaLabelFromIndex(index);
    if (formulaOperands.has(identifier)) {
      const newIdentifier = getAlphaLabelFromIndex(referencedEntries.length);
      referencedEntries.push({
        ...entry,
        dataGroupId: null,
      });
      updatedFormulaMap[identifier] = newIdentifier;
    }
  }

  // saving a constant formula
  if (referencedEntries.length === 0) {
    return {
      definition: formula.definition,
      referencedMetrics: [],
    };
  }

  return {
    definition: replaceFormulaReferences(formula.definition, updatedFormulaMap),
    referencedMetrics: getReferencedMetricsFromEntries(referencedEntries),
  };
}

export function getReferencedMetricsFromEntries(entries: Array<QuerySuperEntry | MetricEntry>): BehaviorShowClause[] {
  // this should return synced entries (non-hydrated entries)
  return getShowSection({entries})
    .toBookmarkData()
    .map((metric) => omit(metric, `isHidden`, `isExpanded`));
}

export class FormulaParseError extends Error {}
export class FormulaParseVariableError extends FormulaParseError {
  constructor(public variable: string) {
    super(`Unknown variable: ${variable}`);
  }
}

let _parser;

/**
 * Parses a formula and returns the AST.
 */
export function parseFormula(formula: string, availableVariables: string[]) {
  try {
    _parser ||= getFormulaParser();

    const tree = _parser.parse(formula);

    // ensure only variables that are available are used
    for (const token of tree.scan_values((node) => node instanceof Token && node.type === `VAR`)) {
      if (!availableVariables.includes(token.value)) {
        throw new FormulaParseVariableError(token.value);
      }
    }

    return tree;
  } catch (err) {
    if (err instanceof UnexpectedToken) {
      if (err.token.type === `$END`) {
        throw new FormulaParseError(`Incomplete formula`);
      }

      throw new FormulaParseError(`Unexpected token: ${err.token.value}`);
    } else if (err instanceof UnexpectedCharacters) {
      throw new FormulaParseError(`Unexpected character: ${err.char}`);
    }

    throw err;
  }
}

export function getMetricUpdateFromFormulaShowClause(clause: FormulaShowClause): Metric {
  return {
    type: `formula`,
    id: clause.id,
    name: clause.name,
    definition: {
      formula: {
        definition: clause.definition,
        referencedMetrics: clause.referencedMetrics,
      },
      display: clause.display,
      measurement: omit(clause.measurement, [`multiAttribution`]),
    },
  };
}

/**
 * Updates a formula definition by replacing old labels with new labels.
 *
 * @param referenceMap - Mapping of old labels to new labels
 */
export function replaceFormulaReferences(formula: string, referenceMap: Record<string, string>) {
  return formula.replace(/\w+/g, (sub) => {
    // if the label is in the reference map, replace it with the new label. otherwise we'll leave it alone
    return referenceMap[sub] || sub;
  });
}

/** FormulaShowClause is the type that get persisted in Insights bookmarks */
export function getResolvedFormulaShowClause({
  formulaEntry,
  metrics,
  behaviors,
  hydrateSavedBehaviors = true,
}: {
  formulaEntry: QueryFormulaEntry;
  metrics: MetricsResource | null;
  behaviors: BehaviorsResource | null;
  hydrateSavedBehaviors?: boolean;
}): FormulaShowClause | null {
  if (!metrics) {
    return null;
  }

  const resolvedEntry = getResolvedFormulaEntry({
    formulaEntry,
    metricsResource: metrics,
    behaviorsResource: behaviors,
  });

  if (resolvedEntry) {
    let referencedMetrics = resolvedEntry.event.referencedMetrics || [];
    if (!hydrateSavedBehaviors) {
      // If the saved behaviors within formula entry don't have any unsaved changes,
      // they should be consisted of id and type only
      const referencedEntries = getEntries({showClauses: referencedMetrics, behaviorsResource: behaviors});
      referencedMetrics = getReferencedMetricsFromEntries(referencedEntries);
    }
    return {
      ...resolvedEntry.event,
      type: TopLevelMetricType.Formula,
      isExpanded: formulaEntry.isExpanded,
      isHidden: formulaEntry.isHidden,
      display: {
        ...resolvedEntry.display,
        ...formulaEntry.display,
      },
      measurement: {
        cumulative: resolvedEntry.cumulative,
        rolling: resolvedEntry.rolling,
        multiAttribution: resolvedEntry.multiAttribution,
      },
    };
  }
  return null;
}

export function getFormulaEntryName(entry: QueryFormulaEntry, metrics: MetricsResource, behaviors: BehaviorsResource) {
  return getResolvedFormulaShowClause({formulaEntry: entry, metrics, behaviors})?.name;
}

export function getUpdatedFormulaEntry(
  entry: QueryFormulaEntry,
  modifiedParams: Partial<
    Pick<FormulaItem, `name` | `definition` | `referencedMetrics`> &
      Pick<QueryFormulaEntry, `rolling` | `cumulative` | `display`>
  >,
  metrics: MetricsResource,
  behaviors: BehaviorsResource,
): QueryFormulaEntry {
  const resolvedFormulaClause = getResolvedFormulaShowClause({formulaEntry: entry, metrics, behaviors});

  return produce(entry, (draft) => {
    draft.event = {
      ...resolvedFormulaClause,
      ...pick(modifiedParams, [`name`, `definition`, `referencedMetrics`]),
    };
    Object.assign(draft, {
      ...resolvedFormulaClause.measurement,
      ...pick(modifiedParams, [`rolling`, `cumulative`, `display`]),
      isExpanded: true,
    });
  });
}

export function canSaveForPlan(params: {
  currentProject: CurrentProject;
  metrics: MetricsResource;
  entry: QueryFormulaEntry;
}): boolean {
  return Boolean(params.currentProject?.canSaveEntity(RestrictEntitySaving.Formulas));
}

export function getNewInlineEntry(
  sectionEntries: Entry[],
  queryOrigin: `canvas` | `experiment` | `report`,
): FormulaItem {
  const existingFormulaNames = sectionEntries
    .filter((entry): entry is QueryFormulaEntry => {
      return (
        isFormulaEntry(entry) &&
        // if it's a placeholder formula entry, it won't have event yet
        Boolean(entry.event)
      );
    })
    .map((entry) => entry.event.name)
    .filter(Boolean);

  return {
    name: getNextFormulaName({existingFormulaNames}),
    definition: ``,
    // populate referencedMetrics to render referenced entries section for inline formula from canvas
    ...(queryOrigin === `canvas` ? {referencedMetrics: []} : {}),
  };
}

export function getModel(entry: QueryFormulaEntry, metrics: MetricsResource): Model<Metric> | null {
  if (entry.event?.id) {
    if (metrics) {
      return metrics.getOne(entry.event.id);
    } else {
      return {status: ModelStatus.Loading};
    }
  } else {
    return null;
  }
}

export function isUnsavedFormula(entry: QueryFormulaEntry): boolean {
  return isNil(entry.event?.id);
}

export function getSaveDisabledMessage({
  props,
  metrics,
  behaviors,
  currentProject,
  i18n,
}: {
  props: RowProps;
  metrics: MetricsResource;
  behaviors: BehaviorsResource;
  currentProject: CurrentProject;
  i18n: LocalizationStore;
}): string | null {
  const {t} = i18n;
  if (!canSaveForPlan({currentProject, metrics, entry: props.entry})) {
    return t(`query-builder.metrics.formula-cannot-save-current-plan`);
  }

  let referencedMetrics: BehaviorShowClause[];
  if (isUnsavedFormula(props.entry) && props.entry.event) {
    const {name, definition} = props.entry.event;
    ({referencedMetrics} = getUpdatedFormulaAndMetrics(
      {name, definition},
      props.getEntries() as Array<QueryMetricEntry | MetricEntry>,
    ));
  } else {
    referencedMetrics =
      getResolvedFormulaShowClause({
        formulaEntry: props.entry,
        metrics,
        behaviors,
      })?.referencedMetrics || [];
  }
  if (!isUnsavedFormula(props.entry) && props.entry.event && !isModifiedShowClauseFormula(props.entry.event)) {
    return t(`query-builder.metrics.no-unsaved-changes`);
  }
  const usesMultiAttributionMetric = Boolean(referencedMetrics.filter((c) => c.measurement.multiAttribution).length);
  const usesCountUsersOnceMetric = Boolean(referencedMetrics.filter((c) => c.measurement.segmentMethod).length);
  if (usesMultiAttributionMetric) {
    return t(`query-builder.metrics.formula-cannot-save-multi-attribution`);
  } else if (usesCountUsersOnceMetric) {
    return t(`query-builder.metrics.formula-cannot-save-count-users-once`);
  }
  return null;
}

export function getDefinition(
  entry: QueryFormulaEntry,
  metrics: MetricsResource | null,
  behaviors: BehaviorsResource | null,
): string | null {
  const formulaShowClause = getResolvedFormulaShowClause({formulaEntry: entry, metrics, behaviors});
  return formulaShowClause?.definition;
}

export function getReferencedEntries(
  entry: QueryFormulaEntry,
  metrics: MetricsResource | null,
  behaviors: BehaviorsResource | null,
): Array<QueryMetricEntry | MetricEntry> {
  const referencedMetrics = getResolvedFormulaShowClause({
    formulaEntry: entry,
    metrics,
    behaviors,
  })?.referencedMetrics;
  if (referencedMetrics) {
    // return hydrated entries
    return getEntries({showClauses: referencedMetrics, behaviorsResource: behaviors});
  }
  return null;
}

export function getFormulaValidationError(
  props: RowProps,
  metrics: MetricsResource,
  behaviors: BehaviorsResource,
): string | null {
  const model = getModel(props.entry, metrics);

  if (model?.status === ModelStatus.Loading) {
    return null;
  }

  const formulaDefinition = getDefinition(props.entry, metrics, behaviors);

  if (formulaDefinition) {
    const entries = getReferencedEntries(props.entry, metrics, behaviors) || props.getEntries(true);
    const labelsToEntries = Object.fromEntries(entries.map((entry, idx) => [getAlphaLabelFromIndex(idx), entry]));
    const validLabels = Object.entries(labelsToEntries)
      .filter(([_, entry]) => !isFormulaEntry(entry))
      .map(([label]) => label);

    try {
      parseFormula(formulaDefinition, validLabels);
    } catch (err) {
      if (err instanceof FormulaParseError) {
        if (err instanceof FormulaParseVariableError) {
          const entry = labelsToEntries[err.variable];

          if (entry && isFormulaEntry(entry)) {
            return `Cannot reference formulas in formulas: ${err.variable}`;
          }
        }

        return err.message;
      }

      // we can ignore non-parser errors as to not interrupt the UX. the formula will be validated again at query
      console.error(err);
    }
  }

  return null;
}

/**
 * Checks if the formula has a validation error.
 */
export function hasFormulaError(props: RowProps, metrics: MetricsResource, behaviors: BehaviorsResource): boolean {
  return (
    getDefinition(props.entry, metrics, behaviors) && getFormulaValidationError(props, metrics, behaviors) !== null
  );
}
